import type {
  SignInInput,
  SignInResponse,
  ForgotPasswordInput,
  ResetPasswordInput,
  ChangePasswordInput,
  StoredAuth,
} from '../../domain/entities/auth.entity';
import {
  extractRoles,
  extractPerkKeys,
} from '../../domain/entities/auth.entity';
import type { AuthApiDatasource } from '../datasources/auth-api.datasource';
import { firebaseAuth } from '@/lib/firebase';
import { useImpersonationStore } from '@/shared/impersonation/impersonation.store';
import {
  signInWithEmailAndPassword,
  signInWithCustomToken as fbSignInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import {
  AuthStorageDatasource,
  reauthenticateAndChangePassword,
} from '@react-apps/auth';

export class AuthRepositoryImpl {
  private readonly api: AuthApiDatasource;
  private readonly storage: AuthStorageDatasource<StoredAuth>;

  constructor(
    api: AuthApiDatasource,
    storage: AuthStorageDatasource<StoredAuth>,
  ) {
    this.api = api;
    this.storage = storage;
  }

  /**
   * Toda entrada passa por aqui ou pelo `signIn`. A máscara de quem estava na
   * aba não sobrevive à troca de sessão: o handoff SSO troca o usuário sem
   * logout, e o novo ator herdaria o `x-act-as-session-id` do anterior.
   */
  async signInWithToken(firebaseToken: string): Promise<SignInResponse> {
    useImpersonationStore.getState().stopImpersonation();
    const data = await this.api.enterPlatform(firebaseToken);
    const accessToken =
      data.accessToken ?? data.tokens?.accessToken ?? firebaseToken;
    const accountId = data.account?.id ?? data.user?.id ?? '';
    const email = data.account?.email ?? data.user?.email ?? '';
    if (accountId) {
      this.storage.save({
        accessToken,
        accountId,
        email,
        roles: extractRoles(data),
      });
    }
    return data;
  }

  async signIn(input: SignInInput): Promise<SignInResponse> {
    const credential = await signInWithEmailAndPassword(
      firebaseAuth,
      input.email,
      input.password,
    );
    // Depois da senha aceita: errar a senha não derruba a máscara de quem já
    // estava na aba.
    useImpersonationStore.getState().stopImpersonation();
    const firebaseToken = await credential.user.getIdToken();
    const data = await this.api.enterPlatform(firebaseToken);
    const accessToken =
      data.accessToken ?? data.tokens?.accessToken ?? firebaseToken;
    const accountId = data.account?.id ?? data.user?.id ?? '';
    const email = data.account?.email ?? data.user?.email ?? input.email;
    if (accountId) {
      this.storage.save({
        accessToken,
        accountId,
        email,
        roles: extractRoles(data),
      });
    }
    return data;
  }

  /**
   * SSO handoff **de entrada**: troca o custom token (emitido pelo gateway para
   * o mesmo uid de quem pediu) por uma sessão Firebase e conclui o login normal.
   * É o outro lado do `createHandoffToken` — a rota de saída daqui para o
   * user-dashboard já existia; a de chegada é esta.
   */
  async signInWithCustomToken(customToken: string): Promise<SignInResponse> {
    const credential = await fbSignInWithCustomToken(firebaseAuth, customToken);
    const firebaseToken = await credential.user.getIdToken();
    return this.signInWithToken(firebaseToken);
  }

  async signInWithGoogle(): Promise<SignInResponse> {
    return this.signInWithPopupOf(new GoogleAuthProvider());
  }

  /**
   * `select_account` faz a pessoa escolher a conta em vez de entrar calada com
   * a que o navegador já tem logada — com conta pessoal e de trabalho no mesmo
   * navegador, a errada é o caso comum.
   */
  async signInWithMicrosoft(): Promise<SignInResponse> {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({ prompt: 'select_account' });
    return this.signInWithPopupOf(provider);
  }

  private async signInWithPopupOf(
    provider: GoogleAuthProvider | OAuthProvider,
  ): Promise<SignInResponse> {
    const credential = await signInWithPopup(firebaseAuth, provider);
    const firebaseToken = await credential.user.getIdToken();
    return this.signInWithToken(firebaseToken);
  }

  async forgotPassword(input: ForgotPasswordInput): Promise<void> {
    return this.api.forgotPassword(input);
  }

  async resetPassword(input: ResetPasswordInput): Promise<void> {
    return this.api.resetPassword(input);
  }

  async changePassword(input: ChangePasswordInput): Promise<void> {
    return this.api.changePassword(input);
  }

  /**
   * Troca obrigatória (senha temporária). A sequência (reautenticar → refresh
   * forçado do token → trocar) é compartilhada; o que é deste app é a chamada
   * HTTP — o pacote não conhece `apiClient` nem a rota.
   */
  reauthenticateAndChangePassword(input: ChangePasswordInput): Promise<string> {
    return reauthenticateAndChangePassword({
      auth: firebaseAuth,
      input,
      changePassword: (payload) => this.api.changePassword(payload),
    });
  }

  createHandoffToken(): Promise<{ customToken: string }> {
    return this.api.createHandoffToken();
  }

  getStoredAuth(): StoredAuth | null {
    return this.storage.get();
  }

  /**
   * Revalida as `roles` do snapshot local contra `/auth/me/perks` — mais leve
   * que refazer o sign-in inteiro. `null` se não houver sessão local pra
   * atualizar (usuário nunca logou neste dispositivo).
   */
  async refreshRoles(): Promise<StoredAuth | null> {
    const stored = this.storage.get();
    if (!stored) return null;
    // Sob máscara o `/auth/me/perks` responde pelo alvo: gravar isso no
    // snapshot daria ao ator as roles do aluno quando ele saísse da máscara.
    if (useImpersonationStore.getState().target) return stored;
    const data = await this.api.getMyPerks();
    // O header sai no interceptor, depois do `await` do token: a máscara pode
    // ter começado no meio, e aí a resposta já é do alvo.
    if (useImpersonationStore.getState().target) return stored;
    const updated: StoredAuth = { ...stored, roles: extractPerkKeys(data) };
    this.storage.save(updated);
    return updated;
  }

  /**
   * Perk keys de quem o backend enxerga nesta request — sob "atuar como", as do
   * alvo. Não toca no snapshot local, que é sempre o do ator.
   */
  async getEffectivePerkKeys(): Promise<string[]> {
    return extractPerkKeys(await this.api.getMyPerks());
  }

  /**
   * Perk keys do usuário logado no scope de **outra** plataforma, sem tocar no
   * snapshot local — que continua sendo o do scope deste app.
   */
  async getMyPerkKeysInScope(scopeId: string): Promise<string[]> {
    return extractPerkKeys(await this.api.getMyPerks(scopeId));
  }

  saveAuth(auth: StoredAuth): void {
    this.storage.save(auth);
  }

  /**
   * Derruba a sessão: apaga o snapshot local e desloga do Firebase.
   *
   * Devolve promise porque **esperar importa** para quem navega ao `/sign-in`
   * em seguida: essa tela entra sozinha ao encontrar sessão Firebase viva
   * (`onAuthStateChanged`), e um `signOut` ainda em vôo faria essa entrada
   * automática disparar com a sessão que acabamos de invalidar. Quem só quer
   * limpar e seguir (logout do menu, reconciliação do guard) chama com `void`.
   */
  async clearAuth(): Promise<void> {
    // Fim de sessão encerra a máscara: senão o próximo login na mesma aba já
    // sairia mascarado, com o `x-act-as-session-id` injetado.
    useImpersonationStore.getState().stopImpersonation();
    this.storage.clear();
    try {
      await firebaseAuth.signOut();
    } catch {
      // Nunca rejeita: quem chama está no meio de tratar OUTRO erro e depende
      // de seguir para exibir a mensagem. Uma falha aqui (persistência do
      // Firebase) não pode virar rejeição que engole o recado da tela — o
      // snapshot local já saiu, que é a parte sob nosso controle.
    }
  }
}
